import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../AppContext';
import { FEELING_LABELS } from '../types';
import Header from '../components/Header';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { getSpendingProfile, categorizedTransactions } = useApp();

  const profile = getSpendingProfile();

  // Map score to label
  const getScoreLabel = (score: number): string => {
    if (score <= 1.5) return FEELING_LABELS[1];
    if (score <= 2.5) return FEELING_LABELS[2];
    if (score <= 3.5) return FEELING_LABELS[3];
    if (score <= 4.5) return FEELING_LABELS[4];
    return FEELING_LABELS[5];
  };

  const scoreLabel = getScoreLabel(profile.overallScore);
  const scoreColor =
    profile.overallScore <= 2 ? 'text-red-600' :
    profile.overallScore <= 3.5 ? 'text-yellow-600' :
    'text-green-600';

  if (categorizedTransactions.length === 0) {
    return (
      <div className="min-h-screen bg-secondary">
        <Header showConnectAccounts />
        <div className="flex flex-col items-center justify-center p-6 min-h-[calc(100vh-80px)]">
          <div className="max-w-2xl text-center bg-white rounded-3xl p-12 shadow-xl">
            <h1 className="text-4xl font-bold mb-6">No Data Yet</h1>
            <p className="text-xl text-gray-700 mb-8">
              Start categorizing your transactions to see your personalized
              spending insights and money mindset profile!
            </p>
            <button
              onClick={() => navigate('/mmt/categorize')}
              className="bg-primary text-white px-12 py-4 rounded-full text-xl font-semibold hover:bg-primary/90 transition-all transform hover:scale-105"
            >
              Start Categorizing
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary">
      <Header showConnectAccounts />
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-5xl font-bold text-white text-center mb-12">
            Your Money Mindset Profile
          </h1>

          {/* Overall Score */}
          <div className="bg-white rounded-3xl p-8 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-center mb-6">
              Tightwad vs. Spendthrift Scale
            </h2>
            <div className="text-center">
              <div className={`text-6xl font-bold mb-4 ${scoreColor}`}>
                {profile.overallScore.toFixed(2)}
              </div>
              <div className={`text-3xl font-semibold mb-6 ${scoreColor}`}>
                {scoreLabel}
              </div>
              <div className="flex justify-between max-w-2xl mx-auto text-sm text-gray-600 border-t-4 border-gray-300 pt-4">
                <span>1.0<br/>Extreme TW</span>
                <span>2.0<br/>TW</span>
                <span>3.0<br/>Neutral</span>
                <span>4.0<br/>ST</span>
                <span>5.0<br/>Extreme ST</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Spending Breakdown */}
            <div className="bg-white rounded-3xl p-8 shadow-xl">
              <h2 className="text-2xl font-bold mb-6">Spending Breakdown</h2>
              <div className="space-y-4">
                <div className="border-b-2 pb-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold text-lg">Total Spending</span>
                    <span className="text-xl font-bold text-primary">
                      ${profile.totalSpending.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Overall Score: {profile.overallScore.toFixed(2)}
                  </div>
                </div>

                {profile.categoryBreakdowns.map((breakdown) => {
                  const percentage = (breakdown.amount / profile.totalSpending) * 100;
                  const feelingLabel = getScoreLabel(breakdown.averageFeeling);
                  const feelingColor =
                    breakdown.averageFeeling <= 2 ? 'text-red-600' :
                    breakdown.averageFeeling <= 3.5 ? 'text-yellow-600' :
                    'text-green-600';

                  return (
                    <div key={breakdown.category} className="border-b pb-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold text-lg">
                          {breakdown.category}
                        </span>
                        <span className="font-bold text-primary">
                          ${breakdown.amount.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          {percentage.toFixed(1)}% of spending
                        </span>
                        <span className={`font-semibold ${feelingColor}`}>
                          {breakdown.averageFeeling.toFixed(2)} - {feelingLabel}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Needs Breakdown */}
            {profile.needsBreakdown.length > 0 && (
              <div className="bg-white rounded-3xl p-8 shadow-xl">
                <h2 className="text-2xl font-bold mb-6">Needs Breakdown</h2>
                <div className="space-y-4">
                  {profile.needsBreakdown.map((breakdown) => {
                    const needsCategory = profile.categoryBreakdowns.find(
                      (c) => c.category === 'Needs'
                    );
                    const needsTotal = needsCategory?.amount || 1;
                    const percentage = (breakdown.amount / needsTotal) * 100;
                    const feelingLabel = getScoreLabel(breakdown.averageFeeling);
                    const feelingColor =
                      breakdown.averageFeeling <= 2 ? 'text-red-600' :
                      breakdown.averageFeeling <= 3.5 ? 'text-yellow-600' :
                      'text-green-600';

                    return (
                      <div key={breakdown.category} className="border-b pb-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-semibold text-lg">
                            {breakdown.category}
                          </span>
                          <span className="font-bold text-primary">
                            ${breakdown.amount.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            {percentage.toFixed(1)}% of needs
                          </span>
                          <span className={`font-semibold ${feelingColor}`}>
                            {breakdown.averageFeeling.toFixed(2)} - {feelingLabel}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-6 mt-12">
            <button
              onClick={() => navigate('/mmt/categorize')}
              className="bg-white text-primary px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
            >
              Categorize More Transactions
            </button>
            <button
              onClick={() => navigate('/')}
              className="bg-accent text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-accent/90 transition-all transform hover:scale-105 shadow-lg"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
